import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Mail, Building2, Store, Users, HelpCircle, Send, Loader2, CheckCircle2, ArrowLeft, Globe } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import api, { apiError } from "@/lib/api";
import { SITE } from "@/config/site";
import { VsMark } from "@/components/Logo";
import { useSEO, breadcrumbLd, webPageLd } from "@/components/Seo";

const QUERY_TYPES = ["General Query", "Business Enquiry", "Platform Support", "Client Support", "Vendor Support", "Staffing Partner Support", "Partnership Enquiry", "Other"];

const SECTIONS = [
  { icon: Building2, t: "Business Enquiries", d: "Partnerships, enterprise plans and platform onboarding." },
  { icon: HelpCircle, t: "Platform Support", d: "Technical assistance for your VS PartnerHub workspace." },
  { icon: Users, t: "Partner Queries", d: "Client, vendor and staffing partner assistance." },
];

export default function Contact() {
  useSEO({
    title: "Contact VS-Solutions | VS PartnerHub Support & Business Enquiries",
    description: "Contact Vijay Software Solutions Pvt Ltd for VS PartnerHub platform support, business enquiries and client, vendor or staffing partner queries.",
    path: "/contact",
    jsonLd: { "@context": "https://schema.org", "@graph": [webPageLd({ title: "Contact VS-Solutions", description: "Contact Vijay Software Solutions Pvt Ltd for VS PartnerHub support and business enquiries.", path: "/contact" }), breadcrumbLd([["Home", "/"], ["Contact", "/contact"]])] },
  });
  const [form, setForm] = React.useState({ name: "", email: "", company: "", query_type: "General Query", subject: "", message: "" });
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState("");
  const [ref, setRef] = React.useState(null);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target?.value ?? e });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setError("");
    try {
      const { data } = await api.post("/public/contact", form);
      setRef(data.reference);
    } catch (err) { setError(apiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5" data-testid="contact-brand">
            <VsMark />
            <div className="leading-tight">
              <div className="font-display font-extrabold text-vs-ink">VS PartnerHub</div>
              <div className="text-[10px] text-slate-500">Powered by Vijay Software Solutions</div>
            </div>
          </Link>
          <div className="flex items-center gap-3">
            <Link to="/login"><Button variant="ghost" className="text-slate-700" data-testid="contact-login-btn">Login</Button></Link>
            <Link to="/register"><Button className="bg-vs-royal hover:bg-vs-blue text-white" data-testid="contact-getstarted-btn">Get Started</Button></Link>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-12 sm:py-16">
        <div className="max-w-2xl">
          <div className="text-xs uppercase tracking-wider font-semibold text-vs-royal">Support & Queries</div>
          <h1 className="font-display font-bold text-3xl sm:text-4xl text-vs-ink mt-3 tracking-tight">Have a question or need assistance?</h1>
          <p className="text-slate-600 mt-4 leading-relaxed">Contact the VS-Solutions team for platform support, business enquiries and partnership queries.</p>
        </div>

        <div className="grid sm:grid-cols-3 gap-4 mt-10">
          {SECTIONS.map((s) => (
            <div key={s.t} className="vs-card p-5" data-testid={`contact-section-${s.t.toLowerCase().replace(/[^a-z]/g, "-")}`}>
              <div className="h-10 w-10 rounded-lg bg-vs-light flex items-center justify-center mb-3"><s.icon className="h-5 w-5 text-vs-royal" /></div>
              <div className="font-display font-semibold text-vs-ink">{s.t}</div>
              <div className="text-sm text-slate-500 mt-1 leading-relaxed">{s.d}</div>
            </div>
          ))}
        </div>

        <div className="grid lg:grid-cols-5 gap-8 mt-10">
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} className="lg:col-span-3 vs-card p-6 sm:p-8">
            {ref ? (
              <div className="text-center py-8" data-testid="contact-success">
                <div className="h-14 w-14 rounded-full bg-emerald-50 flex items-center justify-center mx-auto mb-4"><CheckCircle2 className="h-7 w-7 text-emerald-600" /></div>
                <h2 className="font-display font-bold text-xl text-vs-ink">Your query has been received.</h2>
                <p className="text-sm text-slate-500 mt-2">Our team will respond to <span className="font-medium text-slate-700">{form.email}</span> shortly.</p>
                <div className="mt-4 inline-flex items-center gap-2 rounded-lg bg-slate-50 border border-slate-200 px-4 py-2">
                  <span className="text-xs text-slate-500">Reference ID:</span>
                  <span className="font-mono font-bold text-vs-royal" data-testid="contact-reference">{ref}</span>
                </div>
                <div className="mt-6"><Link to="/"><Button variant="outline" data-testid="contact-back-home"><ArrowLeft className="h-4 w-4 mr-1.5" />Back to home</Button></Link></div>
              </div>
            ) : (
              <form onSubmit={submit} className="space-y-4" data-testid="contact-form">
                <h2 className="font-display font-bold text-lg text-vs-ink">Send us a message</h2>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div><Label>Name</Label><Input value={form.name} onChange={set("name")} required className="mt-1.5" data-testid="contact-name" /></div>
                  <div><Label>Business Email</Label><Input type="email" value={form.email} onChange={set("email")} required className="mt-1.5" data-testid="contact-email" /></div>
                  <div><Label>Company</Label><Input value={form.company} onChange={set("company")} className="mt-1.5" data-testid="contact-company" /></div>
                  <div><Label>Query Type</Label>
                    <Select value={form.query_type} onValueChange={(v) => setForm({ ...form, query_type: v })}>
                      <SelectTrigger className="mt-1.5 bg-white" data-testid="contact-query-type"><SelectValue /></SelectTrigger>
                      <SelectContent className="bg-white">{QUERY_TYPES.map((q) => <SelectItem key={q} value={q}>{q}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                </div>
                <div><Label>Subject</Label><Input value={form.subject} onChange={set("subject")} required className="mt-1.5" data-testid="contact-subject" /></div>
                <div><Label>Message</Label><Textarea value={form.message} onChange={set("message")} required rows={5} className="mt-1.5" data-testid="contact-message" /></div>
                {error && <div className="text-sm text-rose-600 bg-rose-50 border border-rose-100 rounded-lg px-3 py-2" data-testid="contact-error">{error}</div>}
                <Button type="submit" disabled={busy} className="bg-vs-royal hover:bg-vs-blue text-white" data-testid="contact-submit-btn">
                  {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <>Send Query<Send className="ml-1.5 h-4 w-4" /></>}
                </Button>
              </form>
            )}
          </motion.div>

          <div className="lg:col-span-2 space-y-4">
            <div className="vs-card p-6">
              <div className="flex items-center gap-3 mb-3"><div className="h-10 w-10 rounded-lg bg-vs-light flex items-center justify-center"><Globe className="h-5 w-5 text-vs-royal" /></div>
                <div className="font-display font-semibold text-vs-ink">Vijay Software Solutions</div></div>
              <p className="text-sm text-slate-500 leading-relaxed">Technology. Products. Platforms. Digital Transformation.</p>
              <a href={SITE.website} target="_blank" rel="noopener noreferrer" className="mt-3 inline-flex items-center gap-1.5 text-sm font-semibold text-vs-royal hover:gap-2.5 transition-all" data-testid="contact-website">www.vs-solutions.in</a>
            </div>
            <div className="vs-card p-6">
              <div className="flex items-center gap-3 mb-3"><div className="h-10 w-10 rounded-lg bg-vs-light flex items-center justify-center"><Mail className="h-5 w-5 text-vs-royal" /></div>
                <div className="font-display font-semibold text-vs-ink">Already a partner?</div></div>
              <p className="text-sm text-slate-500 leading-relaxed">Sign in to your workspace and raise a support ticket from Help &amp; Support for faster, organization-linked assistance.</p>
              <Link to="/login" className="mt-3 inline-flex items-center gap-1.5 text-sm font-semibold text-vs-royal hover:gap-2.5 transition-all" data-testid="contact-signin-link">Sign in to raise a ticket</Link>
            </div>
          </div>
        </div>
      </main>

      <footer className="border-t border-slate-200 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6 text-center text-xs text-slate-400">© {new Date().getFullYear()} Vijay Software Solutions Pvt Ltd · Powered by Vijay Software Solutions Pvt Ltd</div>
      </footer>
    </div>
  );
}
